/**
 * Right-hand drawer used by every Workforce modal (Add user, Set
 * occupation, Add several). Presentation only: the caller owns the state
 * and the footer actions.
 */
import { Icons } from 'turbotwin-sdk/icons';
import { Icon } from '@/components/Icon';
import type React from 'react';
import { GatedButton } from '@/components/GatedButton';

export function DrawerShell({
  title,
  busy,
  onCancel,
  children,
  onSubmit,
  submitLabel = 'Save',
  canSubmit,
  disabledReason,
  width = 380,
  footer,
}: {
  title: string;
  busy: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  children: React.ReactNode;
  submitLabel?: string;
  canSubmit: boolean;
  disabledReason?: string;
  /** Drawer width in px; the bulk table needs more than a form does. */
  width?: number;
  /** Replaces the default Cancel/Submit footer when given. */
  footer?: React.ReactNode;
}) {
  return (
    <>
      <div
        onClick={onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          zIndex: 40,
        }}
      />
      <aside
        role="dialog"
        aria-label={title}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          maxWidth: '92vw',
          // `--panel` resolves to #0d1320 (dark) / #ffffff (light) so the
          // drawer follows the active theme. The old `--panel-1` was never
          // defined in styles.css so its hardcoded dark fallback won in
          // both themes — drawer rendered navy on light-mode pages.
          background: 'var(--panel)',
          color: 'var(--text)',
          borderLeft: '1px solid var(--border)',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.4)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ fontWeight: 600 }}>{title}</div>
          <button
            type="button"
            className="icon-btn"
            onClick={onCancel}
            aria-label="Close"
            style={{ width: 22, height: 22 }}
          >
            <Icon of={Icons.action.remove} size={14} />
          </button>
        </header>
        <div
          style={{
            padding: 10,
            display: 'grid',
            gap: 4,
            // alignContent: 'start' stops the grid tracks from stretching to
            // fill the body's available height — otherwise rows distribute
            // empty space between them and we get phantom whitespace.
            alignContent: 'start',
            overflowY: 'auto',
            flex: 1,
          }}
        >
          {children}
        </div>
        {footer !== undefined ? footer : (
          <footer
            style={{
              padding: '12px 18px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            <button
              type="button"
              className="btn ghost"
              onClick={onCancel}
              disabled={busy}
            >
              Cancel
            </button>
            <GatedButton
              className="btn primary"
              onClick={onSubmit}
              busy={busy}
              reason={canSubmit ? null : (disabledReason ?? 'Fill in the required fields')}
            >
              {busy ? 'Working…' : submitLabel}
            </GatedButton>
          </footer>
        )}
      </aside>
    </>
  );
}
